package diana.soleil.design.iraniancanadienbazzar.feature.orders

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.CartController
import diana.soleil.design.iraniancanadienbazzar.core.data.OrderRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ProductRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.CartItem
import diana.soleil.design.iraniancanadienbazzar.core.model.Language
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Product detail (Stitch `dd9128c2`): the product + its business identity + add-to-cart state. */
data class ProductDetailUiState(
    val loading: Boolean = true,
    val product: Product? = null,
    val businessName: String = "",
    val error: Boolean = false,
    val quantity: Int = 1,
    /** Adding from a different business than the current cart — the screen warns before replacing. */
    val replacesCart: Boolean = false,
    val added: Boolean = false,
)

class ProductDetailViewModel(
    private val productRepository: ProductRepository,
    private val businessRepository: BusinessRepository,
    private val cart: CartController,
    private val businessId: String,
    private val productId: String,
) : ViewModel() {

    private val _state = MutableStateFlow(ProductDetailUiState())
    val state: StateFlow<ProductDetailUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            val business = businessRepository.getBusiness(businessId)
            val businessName = (business as? DataResult.Success)?.data?.name.orEmpty()
            productRepository.observeProducts(businessId).collect { result ->
                _state.value = when (result) {
                    is DataResult.Success -> {
                        val product = result.data.firstOrNull { it.id == productId }
                        _state.value.copy(
                            loading = false,
                            product = product,
                            businessName = businessName,
                            error = product == null,
                            replacesCart = cart.wouldReplaceCart(businessId),
                        )
                    }
                    is DataResult.Failure -> _state.value.copy(loading = false, error = true)
                }
            }
        }
    }

    fun setQuantity(quantity: Int) {
        _state.value = _state.value.copy(quantity = quantity.coerceIn(1, 99), added = false)
    }

    fun addToCart(language: Language) {
        val s = _state.value
        val product = s.product ?: return
        cart.add(
            CartItem(
                productId = product.id,
                businessId = businessId,
                businessName = s.businessName,
                name = product.name.resolve(language),
                priceCents = product.priceCents,
                quantity = s.quantity,
            ),
        )
        _state.value = s.copy(added = true, replacesCart = false)
    }
}

/** Cart (Stitch `ce45e69a`): live lines + subtotal. Totals are honest — no fake tax/shipping. */
data class CartUiState(
    val items: List<CartItem> = emptyList(),
    val subtotalCents: Long = 0,
) {
    val businessName: String get() = items.firstOrNull()?.businessName.orEmpty()
}

class CartViewModel(
    private val cart: CartController,
) : ViewModel() {

    val state: StateFlow<CartUiState> =
        cart.items.map { items -> CartUiState(items = items, subtotalCents = items.sumOf { it.lineTotalCents }) }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), CartUiState())

    fun setQuantity(productId: String, quantity: Int) = cart.setQuantity(productId, quantity)
    fun remove(productId: String) = cart.remove(productId)
}

/** Checkout (Stitch `f140cdc2`, pickup model): places the order; guests bounce to sign-in. */
data class CheckoutUiState(
    val items: List<CartItem> = emptyList(),
    val subtotalCents: Long = 0,
    val placing: Boolean = false,
    val signInRequired: Boolean = false,
    val failed: Boolean = false,
    /** Set on success — the route navigates to the confirmation. */
    val placedOrderId: String? = null,
)

class CheckoutViewModel(
    private val cart: CartController,
    private val orderRepository: OrderRepository,
    private val analytics: AnalyticsTracker,
) : ViewModel() {

    private val progress = MutableStateFlow(CheckoutUiState())

    val state: StateFlow<CheckoutUiState> =
        combine(cart.items, progress) { items, p ->
            p.copy(items = items, subtotalCents = items.sumOf { it.lineTotalCents })
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), CheckoutUiState())

    fun placeOrder(note: String) {
        if (progress.value.placing || cart.items.value.isEmpty()) return
        viewModelScope.launch {
            progress.value = progress.value.copy(placing = true, failed = false, signInRequired = false)
            when (val result = orderRepository.placeOrder(cart.items.value, note)) {
                is DataResult.Success -> {
                    analytics.track(
                        AnalyticsEvents.ORDER_PLACED,
                        mapOf(AnalyticsParams.BUSINESS_ID to (cart.activeBusinessId ?: "")),
                    )
                    cart.clear()
                    progress.value = progress.value.copy(placing = false, placedOrderId = result.data)
                }
                is DataResult.Failure -> progress.value = progress.value.copy(
                    placing = false,
                    signInRequired = result.error is AppError.Unauthorized,
                    failed = result.error !is AppError.Unauthorized,
                )
            }
        }
    }

    fun consumeGates() {
        progress.value = progress.value.copy(signInRequired = false, placedOrderId = null)
    }
}

/** Order history (Stitch `4d08fbb9`) + confirmation (`d3f50950`). */
data class OrdersUiState(
    val loading: Boolean = true,
    val orders: List<Order> = emptyList(),
    val signInRequired: Boolean = false,
    val error: Boolean = false,
    /** Order ids with an in-flight or failed cancel (buyer may cancel only while pending). */
    val busyIds: Set<String> = emptySet(),
    val failedIds: Set<String> = emptySet(),
)

class OrderHistoryViewModel(
    private val orderRepository: OrderRepository,
) : ViewModel() {
    private val busyIds = MutableStateFlow<Set<String>>(emptySet())
    private val failedIds = MutableStateFlow<Set<String>>(emptySet())

    val state: StateFlow<OrdersUiState> =
        combine(orderRepository.observeMyOrders(), busyIds, failedIds) { result, busy, failed ->
            when (result) {
                is DataResult.Success ->
                    OrdersUiState(loading = false, orders = result.data, busyIds = busy, failedIds = failed)
                is DataResult.Failure -> OrdersUiState(
                    loading = false,
                    signInRequired = result.error is AppError.Unauthorized,
                    error = result.error !is AppError.Unauthorized,
                )
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), OrdersUiState())

    /** Buyer cancels a pending order; the row updates through the live stream. */
    fun cancel(orderId: String) {
        viewModelScope.launch {
            busyIds.value += orderId
            failedIds.value -= orderId
            if (orderRepository.cancelMyOrder(orderId) is DataResult.Failure) failedIds.value += orderId
            busyIds.value -= orderId
        }
    }
}

/**
 * Owner order inbox for one business — the counter-side of pay-at-pickup. Without this surface
 * orders were write-only: buyers checked out into a void. Rules enforce the legal status machine
 * (pending→confirmed→fulfilled; cancel from non-terminal; paymentStatus settles any time).
 */
data class ManageOrdersUiState(
    val loading: Boolean = true,
    val orders: List<Order> = emptyList(),
    val error: Boolean = false,
    val busyIds: Set<String> = emptySet(),
    val failedIds: Set<String> = emptySet(),
) {
    val openCount: Int get() = orders.count { it.status == OrderStatus.Pending || it.status == OrderStatus.Confirmed }
}

class ManageOrdersViewModel(
    private val orderRepository: OrderRepository,
    businessId: String,
) : ViewModel() {
    private val busyIds = MutableStateFlow<Set<String>>(emptySet())
    private val failedIds = MutableStateFlow<Set<String>>(emptySet())

    val state: StateFlow<ManageOrdersUiState> =
        combine(orderRepository.observeBusinessOrders(businessId), busyIds, failedIds) { result, busy, failed ->
            when (result) {
                is DataResult.Success ->
                    ManageOrdersUiState(loading = false, orders = result.data, busyIds = busy, failedIds = failed)
                is DataResult.Failure -> ManageOrdersUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ManageOrdersUiState())

    fun move(orderId: String, status: OrderStatus? = null, paymentStatus: PaymentStatus? = null) {
        viewModelScope.launch {
            busyIds.value += orderId
            failedIds.value -= orderId
            if (orderRepository.updateOrderStatus(orderId, status, paymentStatus) is DataResult.Failure) {
                failedIds.value += orderId
            }
            busyIds.value -= orderId
        }
    }
}

data class OrderConfirmationUiState(
    val loading: Boolean = true,
    val order: Order? = null,
    val error: Boolean = false,
)

class OrderConfirmationViewModel(
    orderRepository: OrderRepository,
    orderId: String,
) : ViewModel() {
    private val _state = MutableStateFlow(OrderConfirmationUiState())
    val state: StateFlow<OrderConfirmationUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            _state.value = when (val result = orderRepository.getOrder(orderId)) {
                is DataResult.Success -> OrderConfirmationUiState(loading = false, order = result.data)
                is DataResult.Failure -> OrderConfirmationUiState(loading = false, error = true)
            }
        }
    }
}

/** Owner's product management (dashboard side): list + add/edit for one owned business. */
data class ManageProductsUiState(
    val loading: Boolean = true,
    val products: List<Product> = emptyList(),
    val saving: Boolean = false,
    /** The last save didn't reach Firestore — the screen shows an inline error by the save button. */
    val saveFailed: Boolean = false,
    val error: Boolean = false,
)

class ManageProductsViewModel(
    private val productRepository: ProductRepository,
    private val businessId: String,
) : ViewModel() {

    private val saving = MutableStateFlow(false)
    private val saveFailed = MutableStateFlow(false)

    val state: StateFlow<ManageProductsUiState> =
        combine(productRepository.observeProducts(businessId), saving, saveFailed) { result, isSaving, failed ->
            when (result) {
                is DataResult.Success -> ManageProductsUiState(
                    loading = false,
                    products = result.data,
                    saving = isSaving,
                    saveFailed = failed,
                )
                is DataResult.Failure -> ManageProductsUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ManageProductsUiState())

    fun save(
        productId: String?,
        nameEn: String,
        nameFa: String,
        priceCents: Long,
        available: Boolean,
        promoted: Boolean,
        nameFr: String = "",
        description: LocalizedText = LocalizedText(),
        property: PropertyDetails? = null,
    ) {
        // Any-language name is enough (a Farsi-only product is legit); the silent EN-only guard
        // made the promote toggle a no-op for such products (PENDING §2 follow-up).
        if ((nameEn.isBlank() && nameFa.isBlank() && nameFr.isBlank()) || priceCents <= 0 || saving.value) return
        viewModelScope.launch {
            saving.value = true
            saveFailed.value = false
            val result = productRepository.upsertProduct(
                businessId = businessId,
                productId = productId,
                name = LocalizedText(
                    en = nameEn.trim().ifEmpty { null },
                    fr = nameFr.trim().ifEmpty { null },
                    fa = nameFa.trim().ifEmpty { null },
                ),
                description = description,
                priceCents = priceCents,
                available = available,
                promoted = promoted,
                property = property,
            )
            if (result is DataResult.Failure) saveFailed.value = true
            saving.value = false
        }
    }
}
